import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  Input,
  MessageBar,
  MessageBarBody,
  Select,
  Spinner,
  Switch,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Toast,
  ToastTitle,
  Toaster,
  useId,
  useToastController,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowSync16Regular,
  DeleteRegular,
  EditRegular,
} from "@fluentui/react-icons";
import { apiCall } from "../../api/services";
import styles from "./Settup.module.css";

/* ------------------------------------------------------------------ */
/* Types (match Authentication.DTOs: RoleDto, PermissionManagementDto, */
/* RolePermissionDto)                                                  */
/* ------------------------------------------------------------------ */

export type AccessView = "Roles" | "Permissions" | "RolePermissions";

interface RolePermission {
  rolePermissionId?: number;
  roleId: number;
  permissionId: number;
  isAllowed?: boolean | null;
  condition?: string | null;
  priority?: number | null;
  source?: string | null;
  permissionCode: string;
  roleName: string;
}

interface RoleItem {
  roleId: number;
  name: string;
  roleCode?: string | null;
  description?: string | null;
  roleType?: string | null;
  module?: string | null;
  isSystemRole?: boolean | null;
  isActive?: boolean | null;
  tenantId: number;
  createdAt?: string | null;
  updatedAt?: string | null;
  rolePermissions?: RolePermission[] | null;
}

interface PermissionItem {
  permissionId: number;
  code: string;
  description?: string | null;
  module?: string | null;
  action?: string | null;
  resource?: string | null;
  level?: string | null;
  scope?: string | null;
  permissionGroup?: string | null;
  tags?: string | null;
  isActive?: boolean | null;
  parentPermissionId?: number | null;
  createdAt?: string | null;
  updatedAt?: string | null;
}

type Intent = "success" | "error" | "warning";
type Notify = (message: string, intent?: Intent) => void;

interface ViewProps {
  token: string;
  notify: Notify;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

type Method = "GET" | "POST" | "PUT" | "DELETE";

/** All role/permission endpoints live in the central auth service. */
const call = async (
  token: string,
  endpoint: string,
  method: Method,
  data?: unknown
): Promise<any> => {
  const res = await apiCall({
    service: "auth",
    endpoint,
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    ...(data !== undefined ? { data } : {}),
  } as any);
  if (res && res.error) {
    throw new Error(typeof res.error === "string" ? res.error : "The request failed");
  }
  return res;
};

/** The API may return a plain array or a reference-preserved { $values: [] }. */
function list<T>(res: any): T[] {
  if (Array.isArray(res)) return res as T[];
  if (res && Array.isArray(res.$values)) return res.$values as T[];
  return [];
}

const msg = (e: unknown) =>
  e instanceof Error && e.message ? e.message : "Something went wrong";

const fmt = (d?: string | null) =>
  d
    ? new Date(d).toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "numeric",
        hour12: true,
      })
    : "N/A";

/** RolePermissionDto requires PermissionCode and RoleName, so always send them. */
const cleanAssignments = (role: RoleItem): RolePermission[] | undefined => {
  if (role.rolePermissions == null) return undefined;
  return list<RolePermission>(role.rolePermissions).map((rp) => ({
    rolePermissionId: rp.rolePermissionId,
    roleId: role.roleId,
    permissionId: rp.permissionId,
    isAllowed: rp.isAllowed ?? true,
    condition: rp.condition ?? null,
    priority: rp.priority ?? null,
    source: rp.source ?? null,
    permissionCode: rp.permissionCode ?? "",
    roleName: role.name,
  }));
};

const FormRow: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <>
    <div
      className={`${styles.map_cell} ${styles.map_cell_custom} ${styles.map_wrap_label} ${styles.textBreak} ${styles.text900}`}
    >
      <label className={`${styles.map_form_label} ${styles.oe_inline}`}>
        {label}
      </label>
    </div>
    <div className={`${styles.map_cell} ${styles.map_cell_custom}`}>
      <div
        className={`${styles.map_field_widget} ${styles.map_field_roomNumber}`}
      >
        <div
          className={`${styles.map_roomNumber_content} ${styles.dinlineFlex} ${styles.w100}`}
        >
          {children}
        </div>
      </div>
    </div>
  </>
);

const Badge: React.FC<{ on: boolean; onText: string; offText: string }> = ({
  on,
  onText,
  offText,
}) => (
  <span
    className={`${styles.accessBadge} ${
      on ? styles.accessBadgeOn : styles.accessBadgeOff
    }`}
  >
    {on ? onText : offText}
  </span>
);

/* ------------------------------------------------------------------ */
/* Roles                                                               */
/* ------------------------------------------------------------------ */

interface RoleForm {
  name: string;
  roleCode: string;
  description: string;
  roleType: string;
  module: string;
  tenantId: string;
  isActive: boolean;
}

const emptyRole = (tenantId: string): RoleForm => ({
  name: "",
  roleCode: "",
  description: "",
  roleType: "",
  module: "",
  tenantId,
  isActive: true,
});

const RolesView: React.FC<ViewProps> = ({ token, notify }) => {
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"list" | "form">("list");
  const [editing, setEditing] = useState<RoleItem | null>(null);
  const [form, setForm] = useState<RoleForm>(emptyRole(""));
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRoles(list<RoleItem>(await call(token, "Role", "GET")));
    } catch (e) {
      notify(msg(e), "error");
    } finally {
      setLoading(false);
    }
  }, [token, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const defaultTenant = roles.find((r) => r.tenantId)?.tenantId;

  const startNew = () => {
    setEditing(null);
    setForm(emptyRole(defaultTenant ? String(defaultTenant) : ""));
    setTab("form");
  };

  const startEdit = (r: RoleItem) => {
    setEditing(r);
    setForm({
      name: r.name ?? "",
      roleCode: r.roleCode ?? "",
      description: r.description ?? "",
      roleType: r.roleType ?? "",
      module: r.module ?? "",
      tenantId: String(r.tenantId ?? ""),
      isActive: r.isActive !== false,
    });
    setTab("form");
  };

  const set = (k: keyof RoleForm, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name.trim()) return notify("Role name is required", "warning");
    const tenantId = Number(form.tenantId);
    if (!Number.isInteger(tenantId) || tenantId <= 0)
      return notify("Enter a valid tenant id", "warning");
    setSaving(true);
    try {
      const scalars = {
        name: form.name.trim(),
        roleCode: form.roleCode.trim() || null,
        description: form.description.trim() || null,
        roleType: form.roleType.trim() || null,
        module: form.module.trim() || null,
        isActive: form.isActive,
        tenantId,
        isSystemRole: editing?.isSystemRole ?? false,
      };
      if (editing) {
        // keep the role's current permission assignments untouched
        const fresh = (await call(token, `Role/${editing.roleId}`, "GET")) as RoleItem;
        const rolePermissions = cleanAssignments({ ...fresh, name: scalars.name });
        await call(token, `Role/${editing.roleId}`, "PUT", {
          roleId: editing.roleId,
          ...scalars,
          ...(rolePermissions ? { rolePermissions } : {}),
        });
        notify(`Role "${scalars.name}" updated`);
      } else {
        await call(token, "Role", "POST", scalars);
        notify(`Role "${scalars.name}" created`);
      }
      await load();
      setTab("list");
      setEditing(null);
    } catch (e) {
      notify(msg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r: RoleItem) => {
    if (r.isSystemRole) return notify("System roles can't be deleted", "warning");
    if (!window.confirm(`Delete role "${r.name}"? This can't be undone.`)) return;
    try {
      await call(token, `Role/${r.roleId}`, "DELETE");
      notify(`Role "${r.name}" deleted`);
      if (selected === r.roleId) setSelected(null);
      await load();
    } catch (e) {
      notify(msg(e), "error");
    }
  };

  const visible = roles.filter((r) =>
    [r.name, r.roleCode, r.module, r.roleType, r.description].some((v) =>
      (v ?? "").toLowerCase().includes(filter.toLowerCase())
    )
  );

  return (
    <div className={`${styles.nH} ${styles.bkK}`}>
      <div className={styles.nH5}>
        <div className={styles.room_settup}>
          <div className={styles.HeaderItb}>
            <TabList
              selectedValue={tab}
              onTabSelect={(_, d) => {
                if (d.value === "form") {
                  if (tab !== "form") startNew();
                } else {
                  setTab("list");
                }
              }}
            >
              <Tab value="list">Roles</Tab>
              <Tab value="form" icon={<Add16Regular />}>
                {editing ? `Edit ${editing.name}` : "Add New Role"}
              </Tab>
            </TabList>
          </div>
        </div>

        {tab === "form" && (
          <>
            <div className={`${styles.mapContent} ${styles.customScrollBar}`}>
              <div
                className={`${styles.map_form_renderer} ${styles.map_form_editable} ${styles.dflex} ${styles.flexcolumn}`}
              >
                <div className={styles.map_form_sheet_bg}>
                  <div className={`${styles.map_form_sheet} ${styles.positionrelative}`}>
                    <div className={`${styles.oe_title} ${styles.mb24}`}>
                      <h1>
                        <div className={`${styles.mapAutocomplete} ${styles.dropdown}`}>
                          <Input
                            type="text"
                            className={`${styles.mapAutocompleteInput} ${styles.map_input}`}
                            placeholder="Role name, e.g. Front Desk Manager"
                            value={form.name}
                            onChange={(_, d) => set("name", d.value)}
                          />
                        </div>
                      </h1>
                    </div>
                    <div className={styles.map_group}>
                      <div className={`${styles.map_inner_group} ${styles.grid} ${styles.collg6}`}>
                        <FormRow label="Role code">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. HMS_FRONT_DESK"
                            value={form.roleCode}
                            onChange={(_, d) => set("roleCode", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Description">
                          <Input
                            className={styles.map_input}
                            placeholder="What this role is for"
                            value={form.description}
                            onChange={(_, d) => set("description", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Role type">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Custom"
                            value={form.roleType}
                            onChange={(_, d) => set("roleType", d.value)}
                          />
                        </FormRow>
                      </div>
                      <div className={`${styles.map_inner_group} ${styles.grid} ${styles.collg6}`}>
                        <FormRow label="Module">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. HMS"
                            value={form.module}
                            onChange={(_, d) => set("module", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Tenant id">
                          <Input
                            className={styles.map_input}
                            type="number"
                            value={form.tenantId}
                            onChange={(_, d) => set("tenantId", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Active">
                          <Switch
                            checked={form.isActive}
                            onChange={(_, d) => set("isActive", d.checked)}
                          />
                        </FormRow>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.nHFooter}>
              <div className={`${styles.map_form_buttons_edit} ${styles.dflex} ${styles.gap1}`}>
                <Button
                  appearance="primary"
                  className={`${styles.btn} ${styles.map_form_button_save}`}
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  className={`${styles.btn} ${styles.map_form_button_cancel}`}
                  onClick={() => {
                    setEditing(null);
                    setTab("list");
                  }}
                >
                  Discard
                </Button>
              </div>
            </div>
          </>
        )}

        {tab === "list" && (
          <div className={styles.sbdocsPreview}>
            <div className={styles.accessToolbar}>
              <div className={styles.accessSearch}>
                <Input
                  placeholder="Search roles"
                  value={filter}
                  onChange={(_, d) => setFilter(d.value)}
                />
              </div>
              <Button size="small" icon={<ArrowSync16Regular />} onClick={() => void load()}>
                Refresh
              </Button>
              {loading && <Spinner size="extra-tiny" />}
            </div>
            <div className={`${styles.fSXvivaresize} ${styles.customScrollBar}`}>
              <div className={styles.FCUdataGrid}>
                <div className={styles.OverflowExgrid}>
                  <Table size="small" role="grid" aria-label="Roles">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className="autoTableCell">ID</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Name</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Code</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Module</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Type</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Status</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Last Updated</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Actions</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visible.map((r) => (
                        <TableRow
                          key={r.roleId}
                          className={r.roleId === selected ? "cat-nhxbackground" : ""}
                          onClick={() => setSelected((p) => (p === r.roleId ? null : r.roleId))}
                        >
                          <TableCell className="autoTableCell">{r.roleId}</TableCell>
                          <TableCell className="autoTableCell">
                            {r.name}{" "}
                            {r.isSystemRole ? <span className={styles.accessBadge}>System</span> : null}
                          </TableCell>
                          <TableCell className="autoTableCell">{r.roleCode ?? ""}</TableCell>
                          <TableCell className="autoTableCell">{r.module ?? ""}</TableCell>
                          <TableCell className="autoTableCell">{r.roleType ?? ""}</TableCell>
                          <TableCell className="autoTableCell">
                            <Badge on={r.isActive !== false} onText="Active" offText="Inactive" />
                          </TableCell>
                          <TableCell className="autoTableCell">{fmt(r.updatedAt ?? r.createdAt)}</TableCell>
                          <TableCell>
                            <div className={styles["action-buttons"]}>
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                aria-label={`Edit ${r.name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(r);
                                }}
                              />
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<DeleteRegular />}
                                aria-label={`Delete ${r.name}`}
                                disabled={!!r.isSystemRole}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void remove(r);
                                }}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && visible.length === 0 && (
                        <TableRow>
                          <TableCell className="autoTableCell">No roles found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Permissions                                                         */
/* ------------------------------------------------------------------ */

interface PermForm {
  code: string;
  description: string;
  module: string;
  action: string;
  resource: string;
  level: string;
  scope: string;
  permissionGroup: string;
  tags: string;
  parentPermissionId: string;
  isActive: boolean;
}

const emptyPerm: PermForm = {
  code: "",
  description: "",
  module: "",
  action: "",
  resource: "",
  level: "",
  scope: "",
  permissionGroup: "",
  tags: "",
  parentPermissionId: "",
  isActive: true,
};

const PermissionsView: React.FC<ViewProps> = ({ token, notify }) => {
  const [perms, setPerms] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"list" | "form">("list");
  const [editing, setEditing] = useState<PermissionItem | null>(null);
  const [form, setForm] = useState<PermForm>(emptyPerm);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPerms(list<PermissionItem>(await call(token, "Permission", "GET")));
    } catch (e) {
      notify(msg(e), "error");
    } finally {
      setLoading(false);
    }
  }, [token, notify]);

  useEffect(() => {
    void load();
  }, [load]);

  const startNew = () => {
    setEditing(null);
    setForm(emptyPerm);
    setTab("form");
  };

  const startEdit = (p: PermissionItem) => {
    setEditing(p);
    setForm({
      code: p.code ?? "",
      description: p.description ?? "",
      module: p.module ?? "",
      action: p.action ?? "",
      resource: p.resource ?? "",
      level: p.level ?? "",
      scope: p.scope ?? "",
      permissionGroup: p.permissionGroup ?? "",
      tags: p.tags ?? "",
      parentPermissionId: p.parentPermissionId ? String(p.parentPermissionId) : "",
      isActive: p.isActive !== false,
    });
    setTab("form");
  };

  const set = (k: keyof PermForm, v: string | boolean) =>
    setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.code.trim()) return notify("Permission code is required", "warning");
    const code = form.code.trim();
    if (
      perms.some(
        (p) => p.code.toLowerCase() === code.toLowerCase() && p.permissionId !== editing?.permissionId
      )
    )
      return notify(`A permission with code "${code}" already exists`, "warning");
    setSaving(true);
    try {
      const body = {
        ...(editing ? { permissionId: editing.permissionId } : {}),
        code,
        description: form.description.trim() || null,
        module: form.module.trim() || null,
        action: form.action.trim() || null,
        resource: form.resource.trim() || null,
        level: form.level.trim() || null,
        scope: form.scope.trim() || null,
        permissionGroup: form.permissionGroup.trim() || null,
        tags: form.tags.trim() || null,
        isActive: form.isActive,
        parentPermissionId: form.parentPermissionId ? Number(form.parentPermissionId) : null,
      };
      if (editing) {
        await call(token, `Permission/${editing.permissionId}`, "PUT", body);
        notify(`Permission ${code} updated`);
      } else {
        await call(token, "Permission", "POST", body);
        notify(`Permission ${code} created`);
      }
      await load();
      setTab("list");
      setEditing(null);
    } catch (e) {
      notify(msg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p: PermissionItem) => {
    if (!window.confirm(`Delete permission "${p.code}"? Roles that use it will lose it.`)) return;
    try {
      await call(token, `Permission/${p.permissionId}`, "DELETE");
      notify(`Permission ${p.code} deleted`);
      if (selected === p.permissionId) setSelected(null);
      await load();
    } catch (e) {
      notify(msg(e), "error");
    }
  };

  const visible = perms.filter((p) =>
    [p.code, p.description, p.module, p.action, p.resource, p.permissionGroup].some((v) =>
      (v ?? "").toLowerCase().includes(filter.toLowerCase())
    )
  );

  return (
    <div className={`${styles.nH} ${styles.bkK}`}>
      <div className={styles.nH5}>
        <div className={styles.room_settup}>
          <div className={styles.HeaderItb}>
            <TabList
              selectedValue={tab}
              onTabSelect={(_, d) => {
                if (d.value === "form") {
                  if (tab !== "form") startNew();
                } else {
                  setTab("list");
                }
              }}
            >
              <Tab value="list">Permissions</Tab>
              <Tab value="form" icon={<Add16Regular />}>
                {editing ? `Edit ${editing.code}` : "Add New Permission"}
              </Tab>
            </TabList>
          </div>
        </div>

        {tab === "form" && (
          <>
            <div className={`${styles.mapContent} ${styles.customScrollBar}`}>
              <div
                className={`${styles.map_form_renderer} ${styles.map_form_editable} ${styles.dflex} ${styles.flexcolumn}`}
              >
                <div className={styles.map_form_sheet_bg}>
                  <div className={`${styles.map_form_sheet} ${styles.positionrelative}`}>
                    <div className={`${styles.oe_title} ${styles.mb24}`}>
                      <h1>
                        <div className={`${styles.mapAutocomplete} ${styles.dropdown}`}>
                          <Input
                            type="text"
                            className={`${styles.mapAutocompleteInput} ${styles.map_input}`}
                            placeholder="Permission code, e.g. hms.reservations.read"
                            value={form.code}
                            onChange={(_, d) => set("code", d.value)}
                          />
                        </div>
                      </h1>
                    </div>
                    <div className={styles.map_group}>
                      <div className={`${styles.map_inner_group} ${styles.grid} ${styles.collg6}`}>
                        <FormRow label="Description">
                          <Input
                            className={styles.map_input}
                            placeholder="What this permission allows"
                            value={form.description}
                            onChange={(_, d) => set("description", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Module">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Reservations"
                            value={form.module}
                            onChange={(_, d) => set("module", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Resource">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Reservation"
                            value={form.resource}
                            onChange={(_, d) => set("resource", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Action">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Create, Delete"
                            value={form.action}
                            onChange={(_, d) => set("action", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Level">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Read, Write, Admin"
                            value={form.level}
                            onChange={(_, d) => set("level", d.value)}
                          />
                        </FormRow>
                      </div>
                      <div className={`${styles.map_inner_group} ${styles.grid} ${styles.collg6}`}>
                        <FormRow label="Scope">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Global, Tenant, User"
                            value={form.scope}
                            onChange={(_, d) => set("scope", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Group">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. Front Office"
                            value={form.permissionGroup}
                            onChange={(_, d) => set("permissionGroup", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Tags">
                          <Input
                            className={styles.map_input}
                            placeholder="e.g. critical,readonly"
                            value={form.tags}
                            onChange={(_, d) => set("tags", d.value)}
                          />
                        </FormRow>
                        <FormRow label="Parent permission">
                          <Select
                            value={form.parentPermissionId}
                            onChange={(_, d) => set("parentPermissionId", d.value)}
                          >
                            <option value="">None</option>
                            {perms
                              .filter((p) => p.permissionId !== editing?.permissionId)
                              .map((p) => (
                                <option key={p.permissionId} value={String(p.permissionId)}>
                                  {p.code}
                                </option>
                              ))}
                          </Select>
                        </FormRow>
                        <FormRow label="Active">
                          <Switch
                            checked={form.isActive}
                            onChange={(_, d) => set("isActive", d.checked)}
                          />
                        </FormRow>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={styles.nHFooter}>
              <div className={`${styles.map_form_buttons_edit} ${styles.dflex} ${styles.gap1}`}>
                <Button
                  appearance="primary"
                  className={`${styles.btn} ${styles.map_form_button_save}`}
                  onClick={save}
                  disabled={saving}
                >
                  {saving ? "Saving..." : "Save"}
                </Button>
                <Button
                  className={`${styles.btn} ${styles.map_form_button_cancel}`}
                  onClick={() => {
                    setEditing(null);
                    setTab("list");
                  }}
                >
                  Discard
                </Button>
              </div>
            </div>
          </>
        )}

        {tab === "list" && (
          <div className={styles.sbdocsPreview}>
            <div className={styles.accessToolbar}>
              <div className={styles.accessSearch}>
                <Input
                  placeholder="Search permissions"
                  value={filter}
                  onChange={(_, d) => setFilter(d.value)}
                />
              </div>
              <Button size="small" icon={<ArrowSync16Regular />} onClick={() => void load()}>
                Refresh
              </Button>
              {loading && <Spinner size="extra-tiny" />}
            </div>
            <div className={`${styles.fSXvivaresize} ${styles.customScrollBar}`}>
              <div className={styles.FCUdataGrid}>
                <div className={styles.OverflowExgrid}>
                  <Table size="small" role="grid" aria-label="Permissions">
                    <TableHeader>
                      <TableRow>
                        <TableHeaderCell className="autoTableCell">ID</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Code</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Module</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Resource</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Action</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Group</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Status</TableHeaderCell>
                        <TableHeaderCell className="autoTableCell">Actions</TableHeaderCell>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visible.map((p) => (
                        <TableRow
                          key={p.permissionId}
                          className={p.permissionId === selected ? "cat-nhxbackground" : ""}
                          onClick={() => setSelected((s) => (s === p.permissionId ? null : p.permissionId))}
                        >
                          <TableCell className="autoTableCell">{p.permissionId}</TableCell>
                          <TableCell className="autoTableCell">
                            <span className={styles.accessPermCode}>{p.code}</span>
                          </TableCell>
                          <TableCell className="autoTableCell">{p.module ?? ""}</TableCell>
                          <TableCell className="autoTableCell">{p.resource ?? ""}</TableCell>
                          <TableCell className="autoTableCell">{p.action ?? ""}</TableCell>
                          <TableCell className="autoTableCell">{p.permissionGroup ?? ""}</TableCell>
                          <TableCell className="autoTableCell">
                            <Badge on={p.isActive !== false} onText="Active" offText="Inactive" />
                          </TableCell>
                          <TableCell>
                            <div className={styles["action-buttons"]}>
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<EditRegular />}
                                aria-label={`Edit ${p.code}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  startEdit(p);
                                }}
                              />
                              <Button
                                appearance="subtle"
                                size="small"
                                icon={<DeleteRegular />}
                                aria-label={`Delete ${p.code}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  void remove(p);
                                }}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {!loading && visible.length === 0 && (
                        <TableRow>
                          <TableCell className="autoTableCell">No permissions found.</TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Assign permissions to a role                                        */
/* ------------------------------------------------------------------ */

interface Grant {
  allowed: boolean;
  priority: string;
  condition: string;
}

const AssignView: React.FC<ViewProps> = ({ token, notify }) => {
  const [roles, setRoles] = useState<RoleItem[]>([]);
  const [perms, setPerms] = useState<PermissionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [roleId, setRoleId] = useState("");
  const [role, setRole] = useState<RoleItem | null>(null);
  const [grants, setGrants] = useState<Record<number, Grant>>({});
  const [existing, setExisting] = useState<Record<number, number>>({});
  const [loadingRole, setLoadingRole] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [unreadable, setUnreadable] = useState(false);
  const [q, setQ] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      setLoading(true);
      try {
        const [r, p] = await Promise.all([
          call(token, "Role", "GET"),
          call(token, "Permission", "GET"),
        ]);
        if (!live) return;
        setRoles(list<RoleItem>(r));
        setPerms(list<PermissionItem>(p));
      } catch (e) {
        if (live) notify(msg(e), "error");
      } finally {
        if (live) setLoading(false);
      }
    })();
    return () => {
      live = false;
    };
  }, [token, notify]);

  const pick = useCallback(
    async (id: string) => {
      setRoleId(id);
      setRole(null);
      setGrants({});
      setExisting({});
      setDirty(false);
      setUnreadable(false);
      if (!id) return;
      setLoadingRole(true);
      try {
        const r = (await call(token, `Role/${id}`, "GET")) as RoleItem;
        if (r?.rolePermissions == null) setUnreadable(true);
        const g: Record<number, Grant> = {};
        const ex: Record<number, number> = {};
        list<RolePermission>(r?.rolePermissions).forEach((rp) => {
          g[rp.permissionId] = {
            allowed: rp.isAllowed !== false,
            priority: rp.priority == null ? "" : String(rp.priority),
            condition: rp.condition ?? "",
          };
          if (rp.rolePermissionId) ex[rp.permissionId] = rp.rolePermissionId;
        });
        setRole(r);
        setGrants(g);
        setExisting(ex);
      } catch (e) {
        notify(msg(e), "error");
      } finally {
        setLoadingRole(false);
      }
    },
    [token, notify]
  );

  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const map = new Map<string, PermissionItem[]>();
    perms
      .filter((p) => p.isActive !== false)
      .filter(
        (p) =>
          !needle ||
          [p.code, p.description, p.module, p.action, p.resource, p.permissionGroup].some((v) =>
            (v ?? "").toLowerCase().includes(needle)
          )
      )
      .forEach((p) => {
        const key = p.module || p.permissionGroup || "General";
        map.set(key, [...(map.get(key) ?? []), p]);
      });
    return Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([name, items]) => [name, [...items].sort((a, b) => a.code.localeCompare(b.code))] as [string, PermissionItem[]]);
  }, [perms, q]);

  const toggle = (p: PermissionItem) => {
    setGrants((g) => {
      const n = { ...g };
      if (n[p.permissionId]) delete n[p.permissionId];
      else n[p.permissionId] = { allowed: true, priority: "", condition: "" };
      return n;
    });
    setDirty(true);
  };

  const toggleGroup = (items: PermissionItem[]) => {
    const all = items.every((p) => grants[p.permissionId]);
    setGrants((g) => {
      const n = { ...g };
      items.forEach((p) => {
        if (all) delete n[p.permissionId];
        else if (!n[p.permissionId]) n[p.permissionId] = { allowed: true, priority: "", condition: "" };
      });
      return n;
    });
    setDirty(true);
  };

  const patch = (id: number, part: Partial<Grant>) => {
    setGrants((g) => (g[id] ? { ...g, [id]: { ...g[id], ...part } } : g));
    setDirty(true);
  };

  const save = async () => {
    if (!role) return;
    for (const g of Object.values(grants)) {
      if (g.priority !== "" && !Number.isInteger(Number(g.priority)))
        return notify("Priority must be a whole number", "warning");
    }
    setSaving(true);
    try {
      const rolePermissions: RolePermission[] = Object.entries(grants).map(([pid, g]) => {
        const id = Number(pid);
        return {
          rolePermissionId: existing[id] ?? 0,
          roleId: role.roleId,
          permissionId: id,
          isAllowed: g.allowed,
          condition: g.condition.trim() || null,
          priority: g.priority === "" ? null : Number(g.priority),
          source: "AdminPanel",
          permissionCode: perms.find((p) => p.permissionId === id)?.code ?? "",
          roleName: role.name,
        };
      });
      await call(token, `Role/${role.roleId}`, "PUT", {
        roleId: role.roleId,
        name: role.name,
        roleCode: role.roleCode ?? null,
        description: role.description ?? null,
        roleType: role.roleType ?? null,
        module: role.module ?? null,
        isSystemRole: role.isSystemRole ?? false,
        isActive: role.isActive ?? true,
        tenantId: role.tenantId,
        rolePermissions,
      });
      notify(`${rolePermissions.length} permission${rolePermissions.length === 1 ? "" : "s"} saved for ${role.name}`);
      await pick(String(role.roleId));
    } catch (e) {
      notify(msg(e), "error");
    } finally {
      setSaving(false);
    }
  };

  const granted = Object.keys(grants).length;

  return (
    <div className={`${styles.nH} ${styles.bkK}`}>
      <div className={styles.nH5}>
        <div className={styles.room_settup}>
          <div className={styles.HeaderItb}>
            <TabList selectedValue="assign">
              <Tab value="assign">Assign Permissions</Tab>
            </TabList>
          </div>
        </div>
        <div className={styles.sbdocsPreview}>
          <div className={styles.accessToolbar}>
            <div className={styles.accessW240}>
              <Select value={roleId} onChange={(_, d) => void pick(d.value)} aria-label="Role">
                <option value="">Select a role...</option>
                {roles.map((r) => (
                  <option key={r.roleId} value={String(r.roleId)}>
                    {r.name}
                  </option>
                ))}
              </Select>
            </div>
            <div className={styles.accessSearch}>
              <Input
                placeholder="Search permissions"
                value={q}
                onChange={(_, d) => setQ(d.value)}
                disabled={!role}
              />
            </div>
            {role && (
              <span className={styles.accessBadge}>
                {granted} of {perms.length} assigned
              </span>
            )}
            <Button
              appearance="primary"
              size="small"
              className={`${styles.btn} ${styles.map_form_button_save}`}
              disabled={!role || !dirty || saving || unreadable}
              onClick={save}
            >
              {saving ? "Saving..." : "Save changes"}
            </Button>
            {(loading || loadingRole) && <Spinner size="extra-tiny" />}
          </div>

          {unreadable && (
            <div className={styles.accessWarn}>
              <MessageBar intent="warning">
                <MessageBarBody>
                  The server did not return this role's current assignments, so saving is turned off to avoid
                  overwriting them.
                </MessageBarBody>
              </MessageBar>
            </div>
          )}
          {role && !unreadable && (
            <div className={styles.accessWarn}>
              <MessageBar intent="info">
                <MessageBarBody>
                  Saving replaces the full permission list for {role.name}. Ticked permissions are granted; set a row to
                  Deny to block it explicitly. Higher priority wins when rules conflict.
                </MessageBarBody>
              </MessageBar>
            </div>
          )}

          <div className={`${styles.accessBody} ${styles.customScrollBar}`}>
            {!role && !loading && <p>Select a role to see and change its permissions.</p>}
            {role &&
              groups.map(([name, items]) => {
                const allOn = items.every((p) => grants[p.permissionId]);
                const someOn = items.some((p) => grants[p.permissionId]);
                return (
                  <div key={name} className={styles.accessGroup}>
                    <div className={styles.accessGroupHead}>
                      <Checkbox
                        checked={allOn ? true : someOn ? "mixed" : false}
                        onChange={() => toggleGroup(items)}
                        label={`${name} (${items.length})`}
                      />
                    </div>
                    {items.map((p) => {
                      const g = grants[p.permissionId];
                      return (
                        <div key={p.permissionId} className={styles.accessPermRow}>
                          <Checkbox checked={!!g} onChange={() => toggle(p)} aria-label={`Grant ${p.code}`} />
                          <div className={styles.accessPermInfo}>
                            <span className={styles.accessPermCode}>{p.code}</span>
                            {p.description ? <span className={styles.accessPermDesc}>{p.description}</span> : null}
                          </div>
                          {g && (
                            <div className={styles.accessPermOpts}>
                              <div className={styles.accessW100}>
                                <Select
                                  size="small"
                                  value={g.allowed ? "allow" : "deny"}
                                  onChange={(_, d) => patch(p.permissionId, { allowed: d.value === "allow" })}
                                  aria-label={`Effect for ${p.code}`}
                                >
                                  <option value="allow">Allow</option>
                                  <option value="deny">Deny</option>
                                </Select>
                              </div>
                              <div className={styles.accessW80}>
                                <Input
                                  size="small"
                                  type="number"
                                  placeholder="Priority"
                                  value={g.priority}
                                  onChange={(_, d) => patch(p.permissionId, { priority: d.value })}
                                  aria-label={`Priority for ${p.code}`}
                                />
                              </div>
                              <div className={styles.accessW200}>
                                <Input
                                  size="small"
                                  placeholder="Condition (optional)"
                                  value={g.condition}
                                  onChange={(_, d) => patch(p.permissionId, { condition: d.value })}
                                  aria-label={`Condition for ${p.code}`}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            {role && groups.length === 0 && <p>No permissions match your search.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Entry point used by Settup.tsx                                      */
/* ------------------------------------------------------------------ */

interface SettupAccessProps {
  view: AccessView;
  token: string;
  /** App key passed to /api/me/permissions?app=... (hospitality = "hms"). */
  appKey?: string;
}

const SettupAccess: React.FC<SettupAccessProps> = ({ view, token, appKey = "hms" }) => {
  const toasterId = useId("access-toaster");
  const { dispatchToast } = useToastController(toasterId);
  const [access, setAccess] = useState<"checking" | "allowed" | "denied">("checking");

  const notify: Notify = useCallback(
    (message, intent = "success") =>
      dispatchToast(
        <Toast>
          <ToastTitle>{message}</ToastTitle>
        </Toast>,
        { intent }
      ),
    [dispatchToast]
  );

  // Who is signed in and what may they do in this app? If the check itself
  // fails we don't lock the screen (the server still enforces every call).
  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const me = await call(token, `me/permissions?app=${encodeURIComponent(appKey)}`, "GET");
        const roles = list<string>(me?.roles).map((r) => r.toUpperCase());
        const perms = list<string>(me?.permissions).map((p) => p.toLowerCase());
        const ok =
          roles.includes(`${appKey}_ADMIN`.toUpperCase()) ||
          perms.includes("*") ||
          perms.includes(`${appKey}.*`.toLowerCase());
        if (live) setAccess(ok ? "allowed" : "denied");
      } catch {
        if (live) setAccess("allowed");
      }
    })();
    return () => {
      live = false;
    };
  }, [token, appKey]);

  return (
    <>
      <Toaster toasterId={toasterId} />
      {access === "checking" && <Spinner size="tiny" label="Checking your access..." />}
      {access === "denied" && (
        <div className={styles.accessWarn}>
          <MessageBar intent="warning">
            <MessageBarBody>
              You don't have permission to manage roles and permissions for this app.
            </MessageBarBody>
          </MessageBar>
        </div>
      )}
      {access === "allowed" && view === "Roles" && <RolesView token={token} notify={notify} />}
      {access === "allowed" && view === "Permissions" && <PermissionsView token={token} notify={notify} />}
      {access === "allowed" && view === "RolePermissions" && <AssignView token={token} notify={notify} />}
    </>
  );
};

export default SettupAccess;